/**
 * P37 (Feb 28, 2026) — ODM Credentials admin page.
 *
 * AZAI super-admin only. Shows two side-by-side cards (CERT + PROD)
 * with hostname / username / masked password / SSH fingerprint /
 * last-test result. Edit modal, Test login button, Make-active button
 * per card.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Pencil, Lightning, ShieldCheck, Warning, ArrowsClockwise,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { odmCredentialsApi } from '../services/api';
import { formatDateTime as fmtDate } from '../lib/utils';
import EditCredentialModal from './odm-credentials/EditCredentialModal';
import HostKeyPinningCard from './odm-credentials/HostKeyPinningCard';

const StatusBadge = ({ status }) => {
  if (status === 'success') {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle size={12} className="mr-1" weight="fill" />Last test OK</Badge>;
  }
  if (status === 'failed') {
    return <Badge className="bg-rose-100 text-rose-800 border-rose-200"><XCircle size={12} className="mr-1" weight="fill" />Last test FAILED</Badge>;
  }
  return <Badge variant="outline" className="border-slate-300 text-slate-500">Never tested</Badge>;
};

const CONFIG_STATUS = {
  configured: { label: 'Configured', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle },
  incomplete: { label: 'Incomplete — password missing', cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Warning },
  password_unreadable: { label: 'Password unreadable — re-enter', cls: 'bg-rose-100 text-rose-800 border-rose-200', Icon: XCircle },
  not_configured: { label: 'Not configured', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Warning },
};

const ConfigStatusBadge = ({ status, environment }) => {
  const cfg = CONFIG_STATUS[status] || CONFIG_STATUS.not_configured;
  const { Icon } = cfg;
  return (
    <Badge className={cfg.cls} data-testid={`config-status-${environment}`}>
      <Icon size={12} className="mr-1" weight="fill" />{cfg.label}
    </Badge>
  );
};

const EnvCard = ({ cred, onEdit, onTest, onActivate, testing, activating }) => {
  const isProd = cred.environment === 'prod';
  return (
    <div
      className={`rounded-2xl border-2 p-6 bg-white transition-shadow hover:shadow-md ${cred.is_active ? (isProd ? 'border-emerald-400' : 'border-amber-400') : 'border-slate-200'}`}
      data-testid={`odm-cred-card-${cred.environment}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isProd ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {isProd ? <ShieldCheck size={22} weight="fill" /> : <Lightning size={22} weight="fill" />}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isProd ? 'Production' : 'Certification (CERT)'}
            </h3>
            <div className="text-xs text-slate-500">
              {isProd ? 'Live Medicaid submissions' : 'Test submissions only'}
            </div>
          </div>
        </div>
        {cred.is_active && (
          <Badge className={isProd ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'} data-testid={`active-badge-${cred.environment}`}>
            ● Active
          </Badge>
        )}
      </div>

      {/* Configuration status (4-state) */}
      <div className="mb-4">
        <ConfigStatusBadge status={cred.config_status} environment={cred.environment} />
      </div>

      {/* Connection details */}
      <dl className="space-y-2.5 text-sm mb-5">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Host</dt>
          <dd className="font-mono text-slate-900 text-right truncate" data-testid={`host-${cred.environment}`}>{cred.host}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Port</dt>
          <dd className="font-mono text-slate-900">{cred.port}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Username</dt>
          <dd className="font-mono text-slate-900 text-right truncate" data-testid={`username-${cred.environment}`}>
            {cred.username || <span className="italic text-slate-400">— not set —</span>}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Password</dt>
          <dd className="font-mono text-slate-900 text-right" data-testid={`password-${cred.environment}`}>
            {cred.password_masked || <span className="italic text-slate-400">— not set —</span>}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">SSH Key</dt>
          <dd className="font-mono text-slate-900 text-right truncate text-xs">
            {cred.has_ssh_key
              ? (cred.ssh_key_fingerprint || <span className="text-emerald-700">configured</span>)
              : <span className="italic text-slate-400">— not set —</span>}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Inbound</dt>
          <dd className="font-mono text-xs text-slate-700 text-right truncate">{cred.inbound_dir}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-500 shrink-0">Outbound</dt>
          <dd className="font-mono text-xs text-slate-700 text-right truncate">{cred.outbound_dir}</dd>
        </div>
      </dl>

      {/* Test status */}
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 mb-4">
        <div className="flex items-center justify-between mb-1">
          <StatusBadge status={cred.last_test_status} />
          <span className="text-xs text-slate-500">{fmtDate(cred.last_test_at)}</span>
        </div>
        {cred.last_test_message && (
          <div className="text-xs text-slate-600 mt-1 line-clamp-2">{cred.last_test_message}</div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(cred)}
          data-testid={`edit-btn-${cred.environment}`}
        >
          <Pencil size={14} className="mr-1.5" />Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTest(cred.environment)}
          disabled={testing || !cred.configured}
          data-testid={`test-btn-${cred.environment}`}
        >
          {testing ? <ArrowsClockwise size={14} className="mr-1.5 animate-spin" /> : <Lightning size={14} className="mr-1.5" />}
          Test login
        </Button>
        {!cred.is_active && cred.configured && (
          <Button
            size="sm"
            className={isProd ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
            onClick={() => onActivate(cred.environment)}
            disabled={activating}
            data-testid={`activate-btn-${cred.environment}`}
          >
            <ShieldCheck size={14} className="mr-1.5" weight="fill" />
            Make active
          </Button>
        )}
        {cred.config_status === 'password_unreadable' && (
          <p className="w-full text-xs text-rose-600 mt-1" data-testid={`password-unreadable-hint-${cred.environment}`}>
            Stored password can't be decrypted with the current key. Re-enter the password to enable the login test.
          </p>
        )}
      </div>
    </div>
  );
};

const OdmCredentialsPage = () => {
  const [creds, setCreds] = useState([]);
  const [activeEnv, setActiveEnv] = useState('cert');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [activating, setActivating] = useState(null);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await odmCredentialsApi.list();
      setCreds(res.data.environments || []);
      setActiveEnv(res.data.active_environment);
    } catch (e) {
      toast.error('Failed to load ODM credentials', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleTest = async (env) => {
    setTesting(env);
    try {
      const res = await odmCredentialsApi.test(env);
      const data = res.data;
      if (data.ok) {
        toast.success(`${env.toUpperCase()} login OK`, { description: data.message });
      } else {
        toast.error(`${env.toUpperCase()} login failed`, { description: data.message });
      }
      refresh();
    } catch (e) {
      toast.error('Test failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(null);
    }
  };

  const handleActivate = async (env) => {
    if (env === 'prod' && !window.confirm(
      'You are about to make PRODUCTION the active ODM environment. '
      + 'All claim submissions will go to LIVE Medicaid. Continue?'
    )) {
      return;
    }
    setActivating(env);
    try {
      await odmCredentialsApi.activate(env);
      toast.success(`Active environment switched to ${env.toUpperCase()}`);
      refresh();
    } catch (e) {
      toast.error('Activation failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setActivating(null);
    }
  };

  const handleSaveEdit = async (env, body) => {
    try {
      await odmCredentialsApi.update(env, body);
      toast.success(`${env.toUpperCase()} credentials saved`);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="odm-creds-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="odm-credentials-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          ODM Credentials
        </h1>
        <p className="text-slate-500 mt-1">
          AZAI-global SFTP credentials for Ohio Medicaid. Stored encrypted at rest, visible to AZAI super-admins only.
        </p>
      </div>

      {/* Active env banner */}
      <div
        className={`p-4 rounded-xl border-2 ${activeEnv === 'prod' ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}
        data-testid="active-env-banner"
      >
        <div className="flex items-center gap-3">
          {activeEnv === 'prod' ? <ShieldCheck size={22} className="text-emerald-700" weight="fill" /> : <Warning size={22} className="text-amber-700" weight="fill" />}
          <div>
            <div className="font-semibold text-slate-900">
              {activeEnv === 'prod' ? 'Submitting to ODM PRODUCTION' : 'Submitting to ODM CERT (test environment)'}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {activeEnv === 'prod'
                ? 'All claim batches go to LIVE Medicaid. Test-batch generators are blocked.'
                : 'Claim batches go to the test environment. Use this for validation only.'}
            </div>
          </div>
        </div>
      </div>

      {/* CERT + PROD cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {creds.map((c) => (
          <EnvCard
            key={c.environment}
            cred={c}
            onEdit={setEditing}
            onTest={handleTest}
            onActivate={handleActivate}
            testing={testing === c.environment}
            activating={activating === c.environment}
          />
        ))}
      </div>

      {/* Host-key security (CERT prerequisite — capture → verify → pin) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <HostKeyPinningCard environment="cert" />
        <HostKeyPinningCard environment="prod" />
      </div>

      {/* Edit modal */}
      {editing && (
        <EditCredentialModal
          cred={editing}
          onCancel={() => setEditing(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
};

export default OdmCredentialsPage;
